import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import StyledQRCode, { StyledQRCodeRef } from '../qr/StyledQRCode';
import GlassCard from '../ui/GlassCard';

interface QRCodeHeroProps {
  profileUrl: string;
  firstName?: string;
  lastName?: string;
}

export default function QRCodeHero({ profileUrl, firstName, lastName }: QRCodeHeroProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<StyledQRCodeRef>(null);

  async function handleShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'MyPink Contact',
          text: t('dashboard.qrCode.shareText'),
          url: profileUrl,
        });
      } catch {
        // User cancelled or share failed, fall back to copy
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleDownload() {
    const fullName = [firstName, lastName].filter(Boolean).join('-');
    const fileName = fullName ? `mypink-${fullName.toLowerCase()}.png` : 'mypink-qrcode.png';
    await qrRef.current?.download({ fileName, firstName, lastName });
  }

  return (
    <GlassCard variant="dark" className="p-4 overflow-hidden">
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
        {/* QR code - top on mobile, right on desktop */}
        <div className="flex-shrink-0 order-first sm:order-last">
          <div className="relative" style={{ width: 246, height: 246 }}>
            {/* White background only for framed QR area */}
            <div
              className="absolute bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 animate-glow"
              style={{
                left: 0,
                top: 0,
                width: 246,
                height: 246,
              }}
            />
            {/* QR code with hearts overflowing onto gradient */}
            <StyledQRCode
              ref={qrRef}
              value={profileUrl}
              size={140}
            />
          </div>
        </div>

        {/* Text content - below QR on mobile, left on desktop */}
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <h2 className="text-xl font-bold text-white mb-1">
            {t('dashboard.qrCode.title')}
          </h2>
          <p className="text-base text-white/80 mb-4">
            {t('dashboard.qrCode.scanMe')}
          </p>

          {/* Action buttons - always horizontal */}
          <div className="flex gap-2 justify-center sm:justify-start">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors whitespace-nowrap"
            >
              <DownloadIcon className="w-4 h-4 flex-shrink-0" />
              {t('dashboard.qrCode.download')}
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors whitespace-nowrap"
            >
              <ShareIcon className="w-4 h-4 flex-shrink-0" />
              {copied ? t('dashboard.qrCode.copied') : t('dashboard.qrCode.share')}
            </button>
          </div>

          {/* Profile URL */}
          <p className="text-white/50 text-xs mt-3 break-all">
            {profileUrl}
          </p>
        </div>
      </div>
    </GlassCard>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    </svg>
  );
}
